import React, { useEffect, useMemo, useState } from "react";
import AsyncSelect from "react-select/async";
import { useNavigate } from "react-router-dom";
import api from "../../../services/apiService";
import { downloadRelatorio } from "../../../services/downloadRelatorioService";
import BotaoSalvar from "../../../components/botao-salvar";
import MensagemErroForm from "../../../components/mensagem-erro-form";
import SeletorCandidatos from "../../../components/seletor-candidatos";
import styles from "./styles.module.css";

const FiltroRelatorioResultadosPage = () => {
  const [erro, setErro] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const [agrupamento, setAgrupamento] = useState("local_votacao");
  const [siglaEstado, setSiglaEstado] = useState("");
  const [municipioSelecionado, setMunicipioSelecionado] = useState({
    label: "Todos",
    value: null,
  });

  const [estados, setEstados] = useState([]);
  const [municipios, setMunicipios] = useState([]);

  const [candidatosSelecionados, setCandidatosSelecionados] = useState([]);
  const [modoExibicao, setModoExibicao] = useState("visualizar");
  const maxCandidatos = modoExibicao === "visualizar" ? 5 : 20;

  const opcoesAgrupamento = [
    { value: "local_votacao", label: "Local de Votação" },
    { value: "zona", label: "Zona" },
    { value: "municipio", label: "Município" },
  ];

  useEffect(() => {
    const carregarEstados = async () => {
      try {
        const response = await api.get("/selects/listar-estados-com-resultados");
        setEstados(response.estados || []);
      } catch (error) {
        setErro(error.message || "Erro ao carregar estados.");
      }
    };

    carregarEstados();
  }, []);

  useEffect(() => {
    setMunicipioSelecionado({ label: "Todos", value: null });
    setMunicipios([]);
    setCandidatosSelecionados([]);

    if (!siglaEstado) {
      return;
    }

    const carregarMunicipios = async () => {
      try {
        const response = await api.get("/selects/listar-municipios-com-resultados", {
          params: { sigla_estado: siglaEstado },
        });

        setMunicipios(response.municipios || []);
      } catch (error) {
        setErro(error.message || "Erro ao carregar municípios.");
      }
    };

    carregarMunicipios();
  }, [siglaEstado, agrupamento]);

  useEffect(() => {
    setCandidatosSelecionados([]);
  }, [modoExibicao]);

  const opcoesMunicipios = useMemo(() => {
    const lista = municipios.map((item) => ({
      label: item.nome_municipio,
      value: item.id_municipio,
    }));

    return [{ label: "Todos", value: null }, ...lista];
  }, [municipios]);

  const handleMunicipioChange = (event) => {
    setMunicipioSelecionado(event || { label: "Todos", value: null });
  };

  const buscarMunicipios = async (entrada) => {
    const texto = (entrada || "").toLowerCase().trim();

    if (!texto) {
      return opcoesMunicipios.slice(0, 100);
    }

    return opcoesMunicipios.filter((item) =>
      item.label.toLowerCase().includes(texto)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro(null);

    if (!siglaEstado) {
      setErro("Selecione um estado.");
      return;
    }

    if (candidatosSelecionados.length > maxCandidatos) {
      setErro(`Máximo de ${maxCandidatos} candidatos atingido.`);
      return;
    }

    if (candidatosSelecionados.length === 0) {
      setErro("Adicione pelo menos um candidato");
      return;
    }

    const filtrosParaEnvio = {
      tipo_agrupamento: agrupamento,
      sigla_estado: siglaEstado,
      id_municipio: municipioSelecionado.value,
      ids_candidatos: candidatosSelecionados.map((c) => c.id_candidato),
    };

    setLoading(true);

    try {
      if (modoExibicao === "download") {
        await downloadRelatorio(filtrosParaEnvio);
      } else {
        const data = await api.post(
          "/resultados/resultados-agrupados",
          filtrosParaEnvio
        );

        navigate("/relatorios/resultados", {
          state: { resultados: data, tipo_agrupamento: agrupamento },
        });
      }
    } catch (error) {
      setErro(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="formContainer">
      <h2 className="titulo">Relatório de Resultados</h2>
      {erro && <MensagemErroForm erro={erro} />}

      <form onSubmit={handleSubmit}>
        <fieldset className="fieldset">
          <legend className="titulo">Filtro do Relatório</legend>

          <div className="formRow">
            <div className="formGroup">
              <label>Modo de Exibição:</label>
              <select
                value={modoExibicao}
                onChange={(e) => setModoExibicao(e.target.value)}
                required
              >
                <option value="visualizar">Visualizar na Tela (Máx: 5 candidatos)</option>
                <option value="download">Baixar Excel (Máx: 20 candidatos)</option>
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Agrupamento:</label>
              <select
                value={agrupamento}
                onChange={(e) => setAgrupamento(e.target.value)}
                required
              >
                {opcoesAgrupamento.map((opcao) => (
                  <option key={opcao.value} value={opcao.value}>
                    {opcao.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Estado:</label>
              <select
                value={siglaEstado}
                onChange={(e) => setSiglaEstado(e.target.value)}
                required
              >
                <option value="">Selecione o estado</option>
                {estados.map((estado) => (
                  <option
                    key={estado.id_estado || estado.sigla_estado}
                    value={estado.sigla_estado}
                  >
                    {estado.sigla_estado}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Município de Votação:</label>
              <AsyncSelect
                cacheOptions
                defaultOptions={opcoesMunicipios}
                loadOptions={buscarMunicipios}
                value={municipioSelecionado}
                onChange={handleMunicipioChange}
                placeholder="Digite o nome do município"
                className={styles.AsyncSelect}
                classNamePrefix="formSelect"
                isDisabled={!siglaEstado}
              />
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <SeletorCandidatos
                siglaEstado={siglaEstado}
                candidatosSelecionados={candidatosSelecionados}
                setCandidatosSelecionados={setCandidatosSelecionados}
                maxCandidatos={maxCandidatos}
              />
            </div>
          </div>
        </fieldset>

        <div className="actions">
          <BotaoSalvar label="Gerar Relatório" loading={loading} />
        </div>
      </form>
    </div>
  );
};

export default FiltroRelatorioResultadosPage;
